import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { cpfValidator } from '../../../interfaces/cpf-validator-algorithm';
import { debounceTime, distinctUntilChanged, from, of, switchMap } from 'rxjs';
import { Patient } from '../../../interfaces/patient';
import { CommonModule } from '@angular/common';
import { PatientService } from '../../../services/patient/patient.service';
import { NgxMaskDirective } from 'ngx-mask';
import { Voucher } from '../../../interfaces/voucher';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { CheckInRequest } from '../../../interfaces/checkin-request';
import { ECheckInClassification } from '../../../enums/e-checkin-classification.enum';
import { EAppointmentCategory } from '../../../enums/e-appointment-category.enum';
import { CheckinService } from '../../../services/checkin/checkin.service';
import { Router } from '@angular/router';
import { VoucherService } from '../../../services/voucher/voucher.service';
import { PasswordService } from '../../../services/password/password.service';
import { AttendanceCycleService } from '../../../services/attendance-cycle/attendance-cycle.service';
import { ApplyVoucher } from '../../../interfaces/apply-voucher';
import { DoctorService } from '../../../services/doctor/doctor.service';
import { Doctor } from '../../../interfaces/doctor';
import { MedicalProcedure } from '../../../interfaces/procedures';
import {
  AddProceduresToCheckInRequest,
  ProceduresToAdd,
} from '../../../interfaces/add-procedures-to-check-in-request';
import { FinishExamCheckInRequest } from '../../../interfaces/finish-exam-checkin-request';
import { EditScheduledProcedure } from '../../../interfaces/edit-scheduled-procedure';
import { AvailableProcedure } from '../../../interfaces/available-procedure';
import { PersonFilter } from '../../../interfaces/person-filter.type';
import { AtendimentoService } from '../../../services/atendimento/atendimento.service';
import { QR_CODE_HRA_RECEPCAO_MAP } from '../../../constants/qr-code-hra.constant';
import { BrandService } from '../../../services/brand/brand.service';
import { filtrarNomeSocial, MENSAGEM_NOME_SOCIAL_INVALIDO, nomeSocialValidator } from '../../../interfaces/nome-social-validator';

@Component({
  selector: 'app-open-attendance-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, NgxMaskDirective],
  // Sem modo escuro proprio: fica no tema claro mesmo com o app escuro.
  host: { class: 'tema-claro', style: 'display: contents' },
  templateUrl: './open-attendance-modal.component.html',
  styleUrl: './open-attendance-modal.component.scss',
})
export class OpenAttendanceModalComponent implements OnInit {
  @Input() show: boolean = false;

  @Output() onModalClose = new EventEmitter<void>();

  patient!: Patient;
  updatedPatientData!: Patient;
  checkinRequest!: CheckInRequest;
  finishCheckInRequest!: CheckInRequest;

  vouchers: Voucher[] = [];
  doctors: Doctor[] = [];
  availableProcedures: AvailableProcedure[] = [];
  selectedSingleProcedureId: number | null = null;
  isAvailableProceduresLoaded: boolean = false;
  procedures: (MedicalProcedure & {
    prescriptionId: number;
    editing?: boolean;
    executor?: string;
    budgetId: number;
    quantity: number;
  })[] = [];

  patientFG!: FormGroup;

  readonly MENSAGEM_NOME_SOCIAL_INVALIDO = MENSAGEM_NOME_SOCIAL_INVALIDO;

  cpfAlreadyExists: boolean = false;
  cpfLoading: boolean = false;
  responsibleCpfAlreadyExists: boolean = false;
  responsibleCpfLoading: boolean = false;
  dontHaveEmail: boolean = false;
  inactiveEmail: boolean = false;
  toggleCheckbox: boolean = false;
  loading: boolean = false;
  disableAddressFields: boolean = true;
  isLoading: boolean = false;
  enableOpenAttendace: boolean = false;
  isCheckboxChecked: boolean = false;
  errorModal: boolean = false;
  successModal: boolean = false;
  confirmOpenAttendance: boolean = false;
  selectedBudgetId: number | null = null;
  showDeleteConfirmation: boolean = false;
  showDeleteScheduledProcedureConfirmation: boolean = false;
  procedureToDelete: any = null;
  showDuplicateProcedureWarning: boolean = false;
  duplicateProcedureNames: string[] = [];

  currentStep: number = 1;
  whereAttendance: number = 0;
  specialtyName: string = '';
  isExamOrGyn: boolean = false;
  budgets: number[] = [];
  showBudgetSearch: boolean = false;
  specialtyPrice: number = 0;
  blackPrice: number = 0;
  specialtyCategory: string = '';
  specialtyId: number = 0;
  discountedPrice: number | null = null;
  owedAmount: number | null = null;
  scheduleClassification: string = '';
  inCaseToRemove: string = '';
  isEditingInCaseToRemove: boolean = false;
  isToRemoveScheduledProcedure: boolean = false;
  doctorIdToUpdate: number = 0;
  loadingText: string = 'Carregando orçamentos';

  selectedBudgetIds: number[] = [];
  showAddBudget: boolean = false;
  showAddSingleProcedure: boolean = false;
  isStranger: boolean = false;
  showResponsibleModal: boolean = false;
  showHraModal: boolean = false;
  qrCodeHraPath: string | null = null;
  pendingSpecialDiscount: boolean = false;
  showStrangerInfosModal: boolean = false;

  private loadingInterval: any;

  public get originalAppointmentPrice(): number {
    if (this.scheduleClassification === 'R') {
      return 0;
    }
    if (this.discountedPrice !== null) {
      return this.discountedPrice;
    }
    return this.specialtyPrice;
  }

  canApplyVoucher: boolean = false;

  ufs: string[] = [
    'AC',
    'AL',
    'AM',
    'AP',
    'BA',
    'CE',
    'DF',
    'ES',
    'GO',
    'MA',
    'MT',
    'MS',
    'MG',
    'PA',
    'PB',
    'PR',
    'PE',
    'PI',
    'RJ',
    'RN',
    'RO',
    'RS',
    'RR',
    'SC',
    'SE',
    'SP',
    'TO',
  ];

  voucherNames: string =
    'Veja os cupons disponíveis na Agenda Integrada ou no Tasy.';
  covenant: string = '';
  category: string = '';
  userName: string = '';
  clinicId: string = '';
  appliedVoucherName: string | null = null;
  errorMessage: string =
    'Ocorreu um erro ao abrir o atendimento. Tente novamente mais tarde ou contate o administrador do sistema.';
  successMessage: string = 'Siga para os próximos passos caso necessário.';
  errorFormModal: boolean = false;
  errorFormMessage: string =
    'Verifique os campos destacados e tente novamente.';
  voucherModal: boolean = false;
  voucherModalTitle: string = '';
  voucherModalMessage: string = '';
  voucherModalSuccess: boolean = false;

  constructor(
    private _fb: FormBuilder,
    private _patientService: PatientService,
    private _localStorageService: LocalStorageService,
    private _checkinService: CheckinService,
    private _router: Router,
    private _passwordService: PasswordService,
    private _voucherService: VoucherService,
    private _doctorService: DoctorService,
    private _atendimentoService: AtendimentoService,
    private _attendanceCycleService: AttendanceCycleService,
    private _brandService: BrandService,
  ) {}

  get isSuperClinica(): boolean {
    return this._brandService.cdEmpresa === '16';
  }

  async ngOnInit() {
    this.patient = await this.getPatient({
      type: 2,
      filter: String(this._localStorageService.get('patientId')),
    });

    this.userName = this._localStorageService.get('login');
    this.clinicId = this._localStorageService.get('currentUnit');

    const qrFile = this.isSuperClinica ? undefined : QR_CODE_HRA_RECEPCAO_MAP[Number(this.clinicId)];
    this.qrCodeHraPath = qrFile ? `assets/images/qrCodes/${qrFile}` : null;

    this.doctors = await this._doctorService.getDoctors();
    this.doctors.forEach((doctor) => {
      doctor.doctorName = doctor.doctorName.replace(/^(Dra|Dr)\.?\s*/i, '');
    });
    this.inCaseToRemove = this._localStorageService.get(
      'inCaseToRemoveProcedure',
    );

    this.populateForm();

    if (this.isStranger) {
      this.patientFG!.get('cpf')?.disable();
    }

    this.patientFG!.updateValueAndValidity();
  }

  requestDelete(procedure: any) {
    this.procedureToDelete = procedure;
    this.showDeleteConfirmation = true;
  }

  requestDeleteScheduledProcedure() {
    this.showDeleteScheduledProcedureConfirmation = true;
  }

  confirmDelete() {
    if (this.procedureToDelete) {
      this.procedures = this.procedures.filter(
        (p) => p !== this.procedureToDelete,
      );
      this.cancelDelete();
    }
  }

  confirmDeleteScheduledProcedure() {
    this.isToRemoveScheduledProcedure = true;
    this.inCaseToRemove = '';
    this.showDeleteScheduledProcedureConfirmation = false;
  }

  cancelDelete() {
    this.procedureToDelete = null;
    this.showDeleteConfirmation = false;
  }

  cancelDeleteScheduledProcedure() {
    this.isToRemoveScheduledProcedure = false;
    this.showDeleteScheduledProcedureConfirmation = false;
  }

  async getPatient(filter: PersonFilter): Promise<Patient> {
    return await this._patientService.getPersonByFilter(filter);
  }

  populateForm() {
    const patient = this.patient;

    const getPatientPhone = (phone?: string) =>
      phone ? (phone.startsWith('55') ? phone.substring(2) : phone) : '';

    const getPatientGender = () =>
      patient.gender!.toLowerCase() === 'm'
        ? 'masculino'
        : patient.gender!.toLowerCase() === 'f'
          ? 'feminino'
          : 'indefinido';

    if (!!patient.responsibleDocument) {
      this.responsibleCpfAlreadyExists = true;
    }

    this.disableAddressFields = patient.address.zipcode?.length !== 8;

    this.patientFG = this._fb.group(
      {
        dontHaveEmail: [],
        nome: [
          patient.name ?? '',
          [
            Validators.required,
            Validators.pattern(/^[A-Za-zÀ-ÖØ-öø-ÿ\s]{10,}$/),
          ],
        ],
        cpf: [
          patient.document ?? '',
          [
            Validators.required,
            Validators.pattern('[0-9]{11}'),
            cpfValidator(),
          ],
        ],
        sexo: [getPatientGender(), [Validators.required]],
        telefone1: [
          getPatientPhone(patient.phone),
          [Validators.required, Validators.pattern('[0-9]{10,11}')],
        ],
        dtNascimento: [
          this.validateBirthdate(patient.birthDate.toString()),
          [
            Validators.required,
            Validators.pattern(
              '^(19[0-9][0-9]|20[0-9][0-9])-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$',
            ),
          ],
        ],
        estadoCivil: [patient.maritalStatus ?? '', []],
        patientEmail: [
          patient.email ?? '',
          [Validators.required, Validators.email],
        ],
        nomeSocial: [patient.socialName ?? '', [nomeSocialValidator()]],
        stranger: [this.isStranger],
        documentoEstrangeiro: ['RNM_RNE'],
        rne: [patient.rne ?? '', []],
        passport: [patient.passport ?? '', []],
        cep: [patient.address.zipcode ?? '', [Validators.required]],
        numero: [
          {
            value: String(patient!.address.number),
            disabled: this.disableAddressFields,
          },
          [Validators.required, Validators.pattern(/^[0-9]\d{0,7}$/)],
        ],
        estado: [
          {
            value: patient.address.state ?? '',
            disabled: this.disableAddressFields,
          },
          [Validators.required],
        ],
        cidade: [
          {
            value: patient.address.city ?? '',
            disabled: this.disableAddressFields,
          },
          [Validators.required],
        ],
        endereco: [
          {
            value: patient.address.street ?? '',
            disabled: this.disableAddressFields,
          },
          [Validators.required],
        ],
        bairro: [
          {
            value: patient.address.neighborhood ?? '',
            disabled: this.disableAddressFields,
          },
          [Validators.required],
        ],
        complemento: [
          {
            value: patient.address.complement ?? '',
            disabled: this.disableAddressFields,
          },
        ],
        nomeResponsavel: [
          patient.responsibleName ?? '',
          [Validators.pattern(/^[A-Za-zÀ-ÖØ-öø-ÿ\s]{10,}$/)],
        ],
        cpfResponsavel: [
          patient.responsibleDocument ?? '',
          [Validators.pattern('[0-9]{11}')],
        ],
        selectedVoucher: [null],
      },
      {
        validators: [this.strangerDocumentValidator()],
      },
    );

    if (this.isSameCpfOfPatient()) {
      this.patientFG!.get('cpfResponsavel')!.setValue('');
      this.patientFG!.get('nomeResponsavel')!.setValue('');
    }

    this.patientFG!.get('cpf')!
      .valueChanges.pipe(
        debounceTime(800),
        distinctUntilChanged(),
        switchMap((value: string) => {
          if (value.length === 11) {
            this.cpfLoading = true;
            return from(
              this._patientService.getPersonByDocument(
                this.patientFG.get('cpf')?.value,
              ),
            );
          } else {
            return of([]);
          }
        }),
      )
      .subscribe((patients) => {
        this.cpfLoading = false;
        this.cpfAlreadyExists = !!patients;
      });

    this.patientFG!.get('cpfResponsavel')!
      .valueChanges.pipe(debounceTime(800), distinctUntilChanged())
      .subscribe((value: string) => {
        if (value.length === 11) {
          this.responsibleCpfLoading = true;
          this._patientService
            .getPersonByDocument(this.patientFG.get('cpfResponsavel')?.value)
            .then((patients) => {
              this.responsibleCpfAlreadyExists = !!patients;
              this.responsibleCpfLoading = false;
            })
            .catch(() => {
              this.responsibleCpfLoading = false;
              this.responsibleCpfAlreadyExists = false;
            })
            .finally(() => {
              this.showResponsibleModalInfo();
            });
        } else {
          this.responsibleCpfLoading = false;
          this.responsibleCpfAlreadyExists = false;
        }
      });

    this.patientFG!.get('cep')!
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((cep: string) => {
        const disableField = (field: string) => {
          this.patientFG!.get(field)!.disable();
        };

        const fieldValue = (field: string, value: string) => {
          this.patientFG!.get(field)!.enable();
          this.patientFG!.get(field)!.setValue(value);
        };

        if (cep.length >= 8) {
          fetch(`https://viacep.com.br/ws/${cep}/json/`)
            .then((res) => res.json())
            .then((address) => {
              this.patientFG!.get('numero')!.enable();
              fieldValue('numero', '');
              fieldValue('estado', address.uf);
              fieldValue('cidade', address.localidade);
              fieldValue('endereco', address.logradouro);
              fieldValue('bairro', address.bairro);
              fieldValue('complemento', address.complemento);
            });
        } else {
          [
            'numero',
            'estado',
            'cidade',
            'endereco',
            'bairro',
            'complemento',
          ].forEach((field) => disableField(field));
        }
      });

    if ((!!patient.rne || !!patient.passport) && !patient.document) {
      this.isStranger = true;

      if (!!patient.rne) {
        this.onDocumentoChange('RNM_RNE');
      } else {
        this.onDocumentoChange('PASSPORT');
      }
    }

    this.patientFG.controls['patientEmail'].clearValidators();
    this.patientFG.controls['dontHaveEmail'].valueChanges.subscribe(
      (checked) => {
        const emailControl = this.patientFG.get('patientEmail');

        if (checked) {
          emailControl?.disable();
          emailControl?.clearValidators();
          emailControl?.updateValueAndValidity();
        } else {
          emailControl?.enable();
          emailControl?.setValidators([Validators.required, Validators.email]);
          emailControl?.updateValueAndValidity();
        }

        emailControl?.updateValueAndValidity();
      },
    );
  }

  /** Filtra ao digitar/colar: nunca deixa e-mail, telefone ou número virarem nome social. */
  onNomeSocialInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const filtrado = filtrarNomeSocial(input.value);
    input.value = filtrado;
    this.patientFG.get('nomeSocial')?.setValue(filtrado);
  }

  private strangerDocumentValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const stranger = group.get('stranger')?.value;
      const tipo = group.get('documentoEstrangeiro')?.value;
      const rne = group.get('rne')?.value;
      const passport = group.get('passport')?.value;

      if (!stranger) {
        return null;
      }

      if (tipo === 'RNM_RNE') {
        if (!rne) {
          return { rneRequired: true };
        }

        const rneRegex = /^[a-zA-Z0-9]{6,20}$/;
        if (!rneRegex.test(rne)) {
          return { rneInvalid: true };
        }
      }

      if (tipo === 'PASSPORT') {
        if (!passport) {
          return { passportRequired: true };
        }

        const passportRegex = /^[a-zA-Z0-9]{6,20}$/;
        if (!passportRegex.test(passport)) {
          return { passportInvalid: true };
        }
      }

      return null;
    };
  }

  /*updateResponsibleValidators() {
    const isUnderAge = this.isUnderAge();
    const nomeResponsavelControl = this.patientFG.get('nomeResponsavel');
    const cpfResponsavelControl = this.patientFG.get('cpfResponsavel');
    if (isUnderAge || this.isStranger) {
      nomeResponsavelControl?.setValidators([Validators.required]);
      cpfResponsavelControl?.setValidators([
        Validators.required,
        Validators.pattern('[0-9]{11}'),
        cpfValidator(),
      ]);
    } else {
      nomeResponsavelControl?.clearValidators();
      cpfResponsavelControl?.clearValidators();
    }
    nomeResponsavelControl?.updateValueAndValidity();
    cpfResponsavelControl?.updateValueAndValidity();
  }*/

  responsibleCpfHandle(): boolean {
    return (
      this.isUnderAge() &&
      (this.isFieldInvalid('cpfResponsavel') ||
        !this.responsibleCpfAlreadyExists ||
        this.isSameCpfOfPatient())
    );
  }

  validateBirthdate(birthdate?: string) {
    if (birthdate) {
      birthdate = birthdate.replace(/T.*/, '');
      var dateFormat = /^\d{4}-\d{1,2}-\d{1,2}$/;
      if (!birthdate.match(dateFormat)) {
        return '';
      }
      var parts = birthdate.split('-');
      var year = parseInt(parts[0], 10);
      var month = parseInt(parts[1], 10);
      var day = parseInt(parts[2], 10);
      if (year < 1000 || year > 3000 || month == 0 || month > 12) {
        return '';
      }
      var monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      if (year % 400 == 0 || (year % 100 != 0 && year % 4 == 0)) {
        monthLength[1] = 29;
      }
      if (day > 0 && day <= monthLength[month - 1]) {
        return birthdate;
      }
    }
    return '';
  }

  isUnderAge(): boolean {
    if ((this.patientFG!.get('dtNascimento')?.value as string).length > 0) {
      const date = this.patientFG!.get('dtNascimento')!.value as string;
      return this.getAge(date) < 18;
    }
    return false;
  }

  isFieldInvalid(field: string): boolean {
    const control = this.patientFG.get(field);

    if (!control || control.disabled) {
      return false;
    }

    const interacted = control.touched || control.dirty;

    if (!interacted) {
      return false;
    }

    // erros diretos do control (Validators.required, parttern, etc...)
    if (control.errors) {
      return true;
    }

    // erros vindos do formgruop (validação cruzada)
    const groupErrors = this.patientFG.errors;
    const mappedErrors = this.groupErrorMap[field];

    if (groupErrors && mappedErrors) {
      return mappedErrors.some((errorKey) => !!groupErrors[errorKey]);
    }

    return false;
  }

  private groupErrorMap: Record<string, string[]> = {
    rne: ['rneRequired', 'rneInvalid'],
    passport: ['passportRequired', 'passportInvalid'],
  };

  getAge(date: string) {
    var today = new Date();
    var birthDate = new Date(date);
    var age = today.getFullYear() - birthDate.getFullYear();
    var m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  isSameCpfOfPatient(): boolean {
    const cpf = this.patientFG.get('cpf')?.value;
    const cpfResp = this.patientFG.get('cpfResponsavel')?.value;

    if (!cpf || !cpfResp) {
      return false;
    }

    return cpf === cpfResp;
  }

  isUnderAgeFormInvalid(): boolean {
    return (
      this.isUnderAge() &&
      ((this.patientFG.get('nomeResponsavel')!.value as string).length === 0 ||
        (this.patientFG.get('cpfResponsavel')!.value as string).length === 0 ||
        this.responsibleCpfLoading ||
        !this.responsibleCpfAlreadyExists)
    );
  }

  disabledCPFField(): boolean {
    return !!this.patientFG.get('cpf')?.disable();
  }

  isButtonInactive(): boolean {
    const cpfResp = (this.patientFG.get('cpfResponsavel')?.value ?? '').trim();
    const hasCpfResp = cpfResp.length > 0;

    const isCpfRespRequired = this.isUnderAge() || this.isStranger;

    // ❌ caso obrigatório e não existe
    const invalidRequiredCpf =
      isCpfRespRequired && !this.responsibleCpfAlreadyExists;

    // ❌ caso opcional, mas digitou e não existe
    const invalidOptionalCpf =
      !isCpfRespRequired && hasCpfResp && !this.responsibleCpfAlreadyExists;

    return (
      this.patientFG.invalid ||
      this.cpfLoading ||
      this.isSameCpfOfPatient() ||
      this.responsibleCpfLoading ||
      invalidRequiredCpf ||
      invalidOptionalCpf
    );
  }

  toggleEdit(procedure: any) {
    procedure.editing = !procedure.editing;
  }

  toggleEditInCaseToRemove() {
    this.isEditingInCaseToRemove = true;
  }

  incrementQuantity(procedure: MedicalProcedure & { quantity: number }) {
    procedure.quantity++;
  }

  decrementQuantity(procedure: MedicalProcedure & { quantity: number }) {
    if (procedure.quantity > 1) {
      procedure.quantity--;
    }
  }

  async updateScheduledProcedureExecutor() {
    if (this.isEditingInCaseToRemove) {
      try {
        this.isLoading = true;

        const request: EditScheduledProcedure = {
          doctorId: Number(this.doctorIdToUpdate),
          prescriptionId: Number(
            this._localStorageService.get('prescriptionId'),
          ),
          scheduleId: Number(this._localStorageService.get('scheduleId')),
        };

        await this._checkinService.updateScheduledProcedureExecutor(request);
      } catch (error) {
        console.error(error);
        this.isLoading = false;
        this.isEditingInCaseToRemove = false;
        this.errorMessage = 'Erro ao editar o médico do procedimento.';
        this.errorModal = true;
      } finally {
        this.isLoading = false;
        this.isEditingInCaseToRemove = false;
      }
    }
  }

  cpfIsAlreadyCorrectlyFilled(): boolean {
    return !!this.patientFG.get('cpf')?.valid || this.isStranger;
  }

  nextStep() {
    if (this.currentStep < 9) this.currentStep++;
  }

  startLoadingAnimation() {
    let point = 0;

    this.loadingInterval = setInterval(() => {
      point = (point + 1) % 4;
      this.loadingText = 'Carregando orçamentos' + '.'.repeat(point);
    }, 400);
  }

  stopLoadingAnimation() {
    clearInterval(this.loadingInterval);
    this.loadingText = 'Carregando orçamentos';
  }

  showResponsibleModalInfo() {
    if (!this.responsibleCpfAlreadyExists) {
      this.showResponsibleModal = true;
    }
  }

  closeResponsibleModal() {
    this.showResponsibleModal = false;
  }

  toggleIsStranger(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.isStranger = checked;

    const cpf = this.patientFG.get('cpf');
    const rne = this.patientFG.get('rne');
    const passport = this.patientFG.get('passport');
    const nomeResp = this.patientFG.get('nomeResponsavel');
    const cpfResp = this.patientFG.get('cpfResponsavel');

    if (checked) {
      cpf?.removeValidators(Validators.required);
      cpf?.reset();
      cpf?.disable();

      rne?.enable();
      passport?.enable();

      nomeResp?.addValidators(Validators.required);
      cpfResp?.addValidators(Validators.required);

      this.showStrangerInfosModal = true;
    } else {
      cpf?.addValidators(Validators.required);
      cpf?.enable();

      rne?.reset();
      rne?.disable();

      passport?.reset();
      passport?.disable();

      nomeResp?.removeValidators(Validators.required);
      cpfResp?.removeValidators(Validators.required);
    }

    cpf?.updateValueAndValidity();
    rne?.updateValueAndValidity();
    passport?.updateValueAndValidity();
    nomeResp?.updateValueAndValidity();
    cpfResp?.updateValueAndValidity();
  }

  closeStrangerInfosModal() {
    this.showStrangerInfosModal = false;
  }

  onDocumentoChange(tipo: 'RNM_RNE' | 'PASSPORT') {
    if (tipo === 'RNM_RNE') {
      this.patientFG.get('rne')?.enable();
      this.patientFG.get('passport')?.reset();
      this.patientFG.get('passport')?.disable();
      this.patientFG.get('documentoEstrangeiro')?.setValue('RNM_RNE');
    } else {
      this.patientFG.get('passport')?.enable();
      this.patientFG.get('rne')?.reset();
      this.patientFG.get('rne')?.disable();
      this.patientFG.get('documentoEstrangeiro')?.setValue('PASSPORT');
    }

    this.patientFG.updateValueAndValidity();
  }

  async getBudgets() {
    try {
      this.isLoading = true;
      this.startLoadingAnimation();

      this.budgets = await this._checkinService.getAllBudgetsIds(
        this.patient.id,
      );
    } catch (error) {
      console.error('Erro ao buscar orçamentos:', error);
      this.budgets = [];
    } finally {
      this.isLoading = false;
      this.stopLoadingAnimation();
    }
  }

  private async fetchOwedAmount() {
    const sentBudgetId = Number(this._localStorageService.get('sentBudgetId'));

    try {
      this.isLoading = true;
      this.owedAmount = await this._checkinService.getOwedAmount(sentBudgetId);
    } catch (error) {
      console.error('Erro ao buscar valor devido:', error);
      this.owedAmount = null;
    } finally {
      this.isLoading = false;
    }
  }

  async skipToFinalStep(): Promise<void> {
    await this.fetchOwedAmount();
    this.currentStep = 6;
  }

  goToAddProceduresStep() {
    this.currentStep = 5;
  }

  async getProceduresFromBudget(): Promise<void> {
    if (this.selectedBudgetId === null) return;

    try {
      this.isLoading = true;

      const budgetId = this.selectedBudgetId;

      const budgetProcedures =
        await this._checkinService.getProceduresFromBudget(budgetId);

      this.procedures = budgetProcedures.map((proc) => ({
        id: proc.procedureId,
        name: proc.procedureName,
        sequence: 0,
        examSequence: 0,
        prescriptionId: 0,
        editing: false,
        executor: '',
        isLaboratorialExam: proc.isLaboratorialExam,
        budgetId,
        quantity: 1,
      }));

      this.selectedBudgetIds = [budgetId];
    } finally {
      this.isLoading = false;
    }
  }

  async addProceduresToCheckIn(skipDuplicateCheck = false): Promise<void> {
    if (this.isLoading) return;

    const hasProcedureWithoutDoctor = this.procedures.some(
      (p) =>
        !p.isLaboratorialExam &&
        (!p.executor || !this.doctors.some((d) => d.doctorName === p.executor)),
    );

    if (hasProcedureWithoutDoctor) {
      this.errorMessage =
        'Adicione um médico executor para cada procedimento antes de prosseguir.';
      this.errorModal = true;
      return;
    }

    if (!skipDuplicateCheck) {
      const duplicateNames: string[] = [];

      // Verifica duplicatas dentro da lista pelo ID
      const seenIds = new Set<number>();
      for (const p of this.procedures) {
        if (seenIds.has(p.id)) {
          if (!duplicateNames.includes(p.name)) duplicateNames.push(p.name);
        }
        seenIds.add(p.id);
      }

      // Verifica procedimentos com quantidade > 1
      for (const p of this.procedures) {
        if (p.quantity > 1 && !duplicateNames.includes(p.name)) {
          duplicateNames.push(p.name);
        }
      }

      // Verifica se o procedimento agendado (inCaseToRemove) duplica algum da lista
      if (this.inCaseToRemove) {
        const scheduledName = this.inCaseToRemove.trim().toLowerCase();
        const match = this.procedures.find(
          (p) => p.name.trim().toLowerCase() === scheduledName,
        );
        if (match && !duplicateNames.includes(match.name)) {
          duplicateNames.push(match.name);
        }
      }

      if (duplicateNames.length > 0) {
        this.duplicateProcedureNames = duplicateNames;
        this.showDuplicateProcedureWarning = true;
        return;
      }
    }

    this.showDuplicateProcedureWarning = false;

    try {
      this.isLoading = true;

      const proceduresToAdd: ProceduresToAdd[] = [];
      for (const p of this.procedures) {
        let doctorId: number;

        if (p.executor) {
          const doctor = this.doctors.find((d) => d.doctorName === p.executor);
          if (!doctor) {
            this.errorMessage = `Médico executor não encontrado para o procedimento: ${p.name}`;
            this.errorModal = true;
            return;
          }
          doctorId = doctor.doctorId;
        } else if (p.isLaboratorialExam) {
          doctorId = 0;
        } else {
          this.errorMessage = `Médico executor é obrigatório para o procedimento: ${p.name}`;
          this.errorModal = true;
          return;
        }

        for (let i = 0; i < p.quantity; i++) {
          proceduresToAdd.push({
            procedureId: p.id,
            doctorId: doctorId,
          });
        }
      }

      const request: AddProceduresToCheckInRequest = {
        prescriptionId: Number(this._localStorageService.get('prescriptionId')),
        procedures: proceduresToAdd,
        tasyUser: this.userName,
        scheduleId: Number(this._localStorageService.get('scheduleId')),
        removeScheduledProcedure: this.isToRemoveScheduledProcedure,
      };

      await this._checkinService.addProceduresToCheckIn(request);

      this.currentStep = 6;
    } catch (error) {
      console.error(error);
      this.errorMessage =
        error instanceof Error
          ? error.message
          : 'Erro ao adicionar procedimentos.';
      this.errorModal = true;
    } finally {
      this.isLoading = false;
    }
  }

  async addAnotherBudget(): Promise<void> {
    if (this.selectedBudgetId === null) return;

    try {
      this.isLoading = true;

      const budgetId = this.selectedBudgetId;

      const budgetProcedures =
        await this._checkinService.getProceduresFromBudget(budgetId);

      const newProcedures = budgetProcedures.map((proc) => ({
        id: proc.procedureId,
        name: proc.procedureName,
        sequence: 0,
        examSequence: 0,
        prescriptionId: 0,
        editing: false,
        executor: '',
        isLaboratorialExam: proc.isLaboratorialExam,
        budgetId,
        quantity: 1,
      }));

      this.procedures.push(...newProcedures);
      this.selectedBudgetIds.push(budgetId);

      this.selectedBudgetId = null;
    } finally {
      this.isLoading = false;
    }
  }

  async downloadExamGuide() {
    this.isLoading = true;

    try {
      const checkInId = Number(this._localStorageService.get('checkInId'));
      const files = await this._checkinService.downloadExamGuide(checkInId);

      if (!files || files.length === 0) {
        throw new Error('Nenhuma guia encontrada.');
      }

      for (const file of files) {
        const blob = this.base64ToBlob(file.data, file.contentType);
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = file.fileName;
        document.body.appendChild(a);
        a.click();

        await new Promise((resolve) => setTimeout(resolve, 500));

        window.URL.revokeObjectURL(url);
        a.remove();

        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    } catch (error) {
      console.error('Erro ao baixar as guias de atendimento:', error);
      this.errorMessage = 'Não foi possível baixar as guias de atendimento.';
      this.errorModal = true;
    } finally {
      this.isLoading = false;
    }
  }

  prevStep() {
    this.enableOpenAttendace = false;
    if (this.currentStep === 5 || this.currentStep === 6) {
      this.procedures = [];
      this.selectedBudgetId = null;
      this.selectedBudgetIds = [];
      this.selectedSingleProcedureId = null;
      this.showAddBudget = false;
      this.showAddSingleProcedure = false;
      this.isAvailableProceduresLoaded = false;
      this.availableProcedures = [];
      this.currentStep = 3;
      return;
    }
    if (this.currentStep === 10) {
      this.currentStep = 6;
    } else if (this.currentStep === 8) {
      this.currentStep = 10;
    } else if (this.currentStep === 7) {
      this.currentStep = 2;
    } else if (this.currentStep === 3) {
      this.currentStep = 2;
    } else if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  closeModal(): void {
    this.show = false;
  }

  closeEmit() {
    this.onModalClose.emit();
  }

  postWhereAttendance(where: number) {
    if (where == 1) {
      this.whereAttendance = 1;
      this.enableOpenAttendace = false;
      this.currentStep = 7;
      return;
    }
    if (where == 2) {
      this.whereAttendance = 2;
    } else {
      this.whereAttendance = 3;
    }
    this.enableOpenAttendace = false;
    this.currentStep = 7;
  }

  async openAttendance() {
    this.isLoading = true;

    try {
      const specialty = (this.specialtyName ?? '').toUpperCase().trim();

      const selectedPassword =
        this._localStorageService.get('selectedPassword');
      if (!selectedPassword?.id) {
        throw new Error('Senha do paciente não encontrada.');
      }

      const scheduleId = Number(this._localStorageService.get('scheduleId'));
      if (!scheduleId) {
        throw new Error('Agendamento não encontrado.');
      }

      if (!this.patient?.id) {
        throw new Error('Paciente não carregado.');
      }

      // Abertura de exame:
      if (this.whereAttendance == 1) {
        const isSpecialConsultation =
          specialty === 'GINECOLOGIA' ||
          this.scheduleClassification === 'Pr' ||
          this.scheduleClassification === '5';

        const request: CheckInRequest = {
          scheduleId: Number(scheduleId),
          checkInClassification: ECheckInClassification.Prescription,
          passwordId: Number(selectedPassword.id),
          personId: String(this.patient.id),
          tasyUser: this.userName,
          appointmentCategory: isSpecialConsultation
            ? EAppointmentCategory.Consultation
            : EAppointmentCategory.Exam,
        };
        this.checkinRequest = request;
        try {
          const checkInResponse = await this._checkinService.openAttendance(
            this.checkinRequest,
          );
          console.log('CheckIn response:', checkInResponse);
          this._localStorageService.set('checkInId', checkInResponse.id);
          this._localStorageService.set(
            'prescriptionId',
            checkInResponse.prescriptionId,
          );
          this.successModal = true;
        } catch (error) {
          console.error('Erro ao abrir atendimento:', error);
          this.errorMessage =
            error instanceof Error
              ? error.message
              : 'Erro inesperado ao abrir atendimento.';
          this.errorModal = true;
        } finally {
          this.isLoading = false;
        }

        // Abertura de consulta por dinheiro:
      } else if (this.whereAttendance == 2) {
        const request: CheckInRequest = {
          scheduleId: Number(scheduleId),
          checkInClassification: ECheckInClassification.Complete,
          passwordId: Number(selectedPassword.id),
          personId: String(this.patient.id),
          tasyUser: this.userName,
          appointmentCategory: EAppointmentCategory.Consultation,
        };
        this.checkinRequest = request;
        try {
          const checkInResponse = await this._checkinService.openAttendance(
            this.checkinRequest,
          );
          console.log('CheckIn response:', checkInResponse);
          this._localStorageService.set('checkInId', checkInResponse.id);
          this._localStorageService.set(
            'prescriptionId',
            checkInResponse.prescriptionId,
          );
          this.successModal = true;
        } catch (error) {
          console.error('Erro ao abrir atendimento:', error);
          this.errorMessage =
            error instanceof Error
              ? error.message
              : 'Erro inesperado ao abrir atendimento.';
          this.errorModal = true;
        } finally {
          this.isLoading = false;
        }

        // Abertura de consulta por cartão/pix:
      } else if (this.whereAttendance == 3) {
        const request: CheckInRequest = {
          scheduleId: Number(scheduleId),
          checkInClassification: ECheckInClassification.Complete,
          passwordId: Number(selectedPassword.id),
          personId: String(this.patient.id),
          tasyUser: this.userName,
          appointmentCategory: EAppointmentCategory.Consultation,
        };
        this.checkinRequest = request;
        try {
          const checkInResponse = await this._checkinService.openAttendance(
            this.checkinRequest,
          );
          console.log('CheckIn response:', checkInResponse);
          this._localStorageService.set('checkInId', checkInResponse.id);
          this._localStorageService.set(
            'prescriptionId',
            checkInResponse.prescriptionId,
          );
          this.successModal = true;
        } catch (error) {
          console.error('Erro ao abrir atendimento:', error);
          this.errorMessage =
            error instanceof Error
              ? error.message
              : 'Erro inesperado ao abrir atendimento.';
          this.errorModal = true;
        } finally {
          this.isLoading = false;
        }
      }
    } catch (error) {
      console.error('Catch: Erro ao abrir atendimento:', error);
      this.errorMessage =
        error instanceof Error
          ? error.message
          : 'Catch: Erro inesperado ao abrir atendimento.';
      this.errorModal = true;
    } finally {
      this.isLoading = false;
    }
  }

  async finishExamCheckIn(applySpecialDiscount: boolean) {
    // Verifica HRA antes de concluir (apenas aviso, não bloqueia). Super Clínica não usa HRA.
    if (!this.isSuperClinica && this.patient?.id && !this.showHraModal) {
      const hraRespondido = await this._atendimentoService.verificarHra(this.patient.id);
      if (!hraRespondido) {
        this.pendingSpecialDiscount = applySpecialDiscount;
        this.showHraModal = true;
        return;
      }
    }
    this.showHraModal = false;

    const request: FinishExamCheckInRequest = {
      checkInId: Number(this._localStorageService.get('checkInId')),
      tasyUser: this.userName,
      specialDiscount: applySpecialDiscount,
    };

    try {
      this.isLoading = true;
      const checkInResponse =
        await this._checkinService.finishExamCheckIn(request);
      console.log('CheckIn response:', checkInResponse);
      this.currentStep = 10;
    } catch (error) {
      console.error('Erro ao abrir atendimento:', error);
      if (error instanceof Error) {
        this.errorMessage = error.message;
      } else {
        this.errorMessage = 'Erro inesperado ao abrir atendimento.';
      }
      this.errorModal = true;
    } finally {
      this.isLoading = false;
    }
  }

  closeErrorModal() {
    this.errorModal = false;
    this.errorFormModal = false;
  }

  closeSuccessModal() {
    this.successModal = false;
    if (this.whereAttendance === 1) {
      this.currentStep = 3;
    } else if (this.whereAttendance === 2 || this.whereAttendance === 3) {
      this._localStorageService.remove('patientDocument');
      this.currentStep = 9;
    } else {
      this.backToDashboard();
    }
  }

  closeVoucherModal() {
    this.voucherModal = false;
  }

  backToDashboard() {
    this._router.navigate(['/home']);
  }

  onCheckboxChange(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    this.isCheckboxChecked = checkbox.checked;
  }

  async finish() {
    try {
      await this._passwordService.finishPassword(
        this.userName,
        0,
        Number(this._localStorageService.get('selectedPassword').id),
        Number(this.clinicId),
      );
    } catch (error) {
      console.log(error);
    } finally {
      try { await this._attendanceCycleService.finish('ATENDIDO'); } catch {}
      let removePassword = this._localStorageService.get('selectedPassword');
      if (
        removePassword.id ==
        Number(this._localStorageService.get('selectedPassword').id)
      ) {
        this._localStorageService.remove('selectedPassword');
      }
      this.backToDashboard();
    }
  }

  getWhereAttendance(): string {
    if (this.whereAttendance == 1) {
      return 'pela Agenda Integrada';
    } else {
      return 'pelo Tasy';
    }
  }

  toggleOpenAttendance() {
    this.enableOpenAttendace = !this.enableOpenAttendace;
  }

  patientInfoChanges(): boolean {
    return !this.compareTwoObjects(this.updatedPatientData!, this.patient!);
  }

  saveNewPatient() {
    const getValue = (field: string): string => {
      const value = this.patientFG?.get(field)?.value;

      if (value === null || value === undefined) {
        return '';
      }

      return String(value).trim();
    };

    const getPatientGender = () =>
      getValue('sexo').toLowerCase() === 'masculino'
        ? 'M'
        : getValue('sexo').toLowerCase() === 'feminino'
          ? 'F'
          : 'I';

    const getPatientMaritalStatus: () => string = () => {
      let estadoCivil = 9;
      switch (getValue('estadoCivil').toLowerCase()) {
        case 'solteiro':
          estadoCivil = 1;
          break;
        case 'casado':
          estadoCivil = 2;
          break;
        case 'divorciado':
          estadoCivil = 3;
          break;
        case 'desquitado':
          estadoCivil = 4;
          break;
        case 'viúvo':
          estadoCivil = 5;
          break;
        case 'outros':
          estadoCivil = 9;
          break;
        case 'separado':
          estadoCivil = 6;
          break;
        case 'concubinato/união estável':
          estadoCivil = 7;
          break;
      }
      return String(estadoCivil);
    };

    const newPatient: Patient = {
      name: getValue('nome'),
      birthDate: new Date(getValue('dtNascimento')),
      document: getValue('cpf'),
      stranger: this.isStranger,
      rne: this.isStranger ? getValue('rne') : '',
      passport: this.isStranger ? getValue('passport') : '',
      phone: getValue('telefone1'),
      gender: getPatientGender(),
      email: this.patientFG.get('dontHaveEmail')?.value
        ? ''
        : getValue('patientEmail'),
      socialName: getValue('nomeSocial'),
      maritalStatus: getPatientMaritalStatus(),
      address: {
        state: getValue('estado'),
        city: getValue('cidade'),
        neighborhood: getValue('bairro'),
        zipcode: getValue('cep'),
        street: getValue('endereco'),
        number: this.patientFG!.get('numero')!.value,
        complement: getValue('complemento'),
      },
      responsibleName: getValue('nomeResponsavel'),
      responsibleDocument: getValue('cpfResponsavel'),
      id: this.patient.id ?? 0,
      age: this.patient.age ?? 0,
    };
    this.updatedPatientData = newPatient;
  }

  compareTwoObjects(a: Object, b: Object) {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  async applyVoucher() {
    const selectedVoucher: Voucher =
      this.patientFG.get('selectedVoucher')?.value;
    if (selectedVoucher) {
      const applyVoucherRequest: ApplyVoucher = {
        patientId: this.patient.id,
        specialtyId: this.specialtyId,
        procedureId: 0,
        category:
          this.specialtyCategory.toUpperCase().trim() === 'CONSULTA'
            ? 'Consultation'
            : 'Exam',
        value: selectedVoucher.voucherValue,
        clinicId: Number(this.clinicId),
        voucherName: selectedVoucher.voucherName,
        indicationId: selectedVoucher.indicationId,
        scheduleId: Number(this._localStorageService.get('scheduleId')),
        type: 0,
      };
      try {
        const result =
          await this._voucherService.applyVoucher(applyVoucherRequest);
        this.appliedVoucherName = result.voucherName;
        this.calculateDiscountedPrice(result.type, result.appliedValue);
        this.voucherModalTitle = 'Sucesso';
        this.voucherModalMessage =
          'Cupom aplicado com sucesso! Lembre de conferir os valores no momento do pagamento.';
        this.voucherModalSuccess = true;
        this.voucherModal = true;
      } catch (error: any) {
        console.error('Error applying voucher:', error);
        this.appliedVoucherName = null;
        this.discountedPrice = null;
        this.voucherModalTitle = 'Erro';
        this.voucherModalMessage =
          error.message ||
          'Não foi possível aplicar o cupom. Tente novamente ou contate o suporte.';
        this.voucherModalSuccess = false;
        this.voucherModal = true;
      }
    }
  }

  calculateDiscountedPrice(voucherType: number, voucherValue: number) {
    const originalPrice =
      this.covenant.toUpperCase() === 'PARTICULAR'
        ? this.blackPrice
        : this.specialtyPrice;
    switch (voucherType) {
      case 1:
        this.discountedPrice = originalPrice - voucherValue;
        break;
      case 2:
        this.discountedPrice = Math.floor(
          Math.floor(originalPrice - originalPrice * (voucherValue / 100)),
        );
        break;
      case 3:
        this.discountedPrice = voucherValue;
        break;
      case 4:
        this.discountedPrice = 0;
        break;
      default:
        this.discountedPrice = originalPrice;
        break;
    }
  }

  async registerPatient() {
    this.isLoading = true;
    this.saveNewPatient();

    try {
      await this._patientService.updatePatient(this.updatedPatientData);

      await this._patientService
        .getVouchersByPersonId(this.patient.id)
        .then(async (res) => {
          if (res && res.length > 0) {
            this.vouchers = res;
          } else {
            this.vouchers = [];
          }
        });

      await this._patientService
        .getPlanByPersonId(this.patient.id)
        .then(async (res) => {
          if (res) {
            this.covenant = res.type;
            this.category = res.name;
          }
        });

      await this._patientService
        .getScheduleAndPriceInfos(
          this.patient.id,
          Number(this._localStorageService.get('scheduleId')),
        )
        .then(async (res) => {
          if (res) {
            this.specialtyPrice = res.specialtyPrice;
            this.specialtyCategory = res.specialtyCategory;
            this.specialtyName = res.specialtyName;
            this.blackPrice = res.blackPrice;
            this.specialtyId = res.specialtyId;
            this.scheduleClassification = res.scheduleClassification;
            if (this.scheduleClassification === 'R') {
              this.specialtyPrice = 0;
              this.blackPrice = 0;
              this.discountedPrice = null;
            }
            const nonVoucherSpecialties: string[] = ['GINECOLOGIA'];
            if (
              res.specialtyCategory.toUpperCase() === 'CONSULTA' &&
              !nonVoucherSpecialties.includes(res.specialtyName.toUpperCase())
            ) {
              this.canApplyVoucher = true;
            }
            if (this.shouldEnableProceduresFlow()) {
              this.canApplyVoucher = true;
              this.isExamOrGyn = true;
            }

            this.currentStep++;
          }
        });
    } catch (err) {
      this.isLoading = false;
      console.log('Erro: ', err);
    } finally {
      this.isLoading = false;
    }
  }

  private shouldEnableProceduresFlow(): boolean {
    const isExam = this.specialtyCategory.toUpperCase().trim() !== 'CONSULTA';
    const isGynecology =
      this.specialtyName.toUpperCase().trim() === 'GINECOLOGIA';

    const specialClassifications = ['Pr', '5'];
    const hasSpecialClassification = specialClassifications.includes(
      this.scheduleClassification,
    );

    return isExam || isGynecology || hasSpecialClassification;
  }

  async getAvailableProcedures(): Promise<void> {
    if (this.isAvailableProceduresLoaded) {
      return;
    }
    try {
      this.isLoading = true;
      this.availableProcedures =
        await this._checkinService.getAvailableProcedures();
      this.isAvailableProceduresLoaded = true;
    } catch (error) {
      console.error('Erro ao buscar procedimentos disponíveis:', error);
      this.errorMessage =
        'Não foi possível carregar os procedimentos disponíveis.';
      this.errorModal = true;
    } finally {
      this.isLoading = false;
    }
  }

  addSingleProcedureToSummary(): void {
    if (this.selectedSingleProcedureId === null) {
      this.errorMessage = 'Selecione um procedimento para adicionar.';
      this.errorModal = true;
      return;
    }

    const selectedProc = this.availableProcedures.find(
      (p) => p.procedureId === this.selectedSingleProcedureId,
    );

    if (selectedProc) {
      const newProcedure: MedicalProcedure & {
        prescriptionId: number;
        editing?: boolean;
        executor?: string;
        budgetId: number;
        quantity: number;
      } = {
        id: selectedProc.procedureId,
        name: selectedProc.procedureName,
        sequence: 0,
        examSequence: 0,
        prescriptionId: 0,
        editing: false,
        executor: '',
        isLaboratorialExam: selectedProc.isLaboratorialExam,
        budgetId: 0,
        quantity: 1,
      };
      this.procedures.push(newProcedure);
      this.selectedSingleProcedureId = null;
      this.toggleAddSingleProcedure();
    } else {
      this.errorMessage = 'Procedimento selecionado não encontrado.';
      this.errorModal = true;
    }
  }

  private base64ToBlob(base64: string, contentType: string): Blob {
    const cleanedBase64 = base64.replace(/\s/g, '').replace(/[\r\n]+/g, '');

    const sliceSize = 1024;
    const buffers: ArrayBuffer[] = [];

    for (let offset = 0; offset < cleanedBase64.length; offset += sliceSize) {
      const slice = cleanedBase64.slice(offset, offset + sliceSize);
      const byteCharacters = atob(slice);

      const byteNumbers = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      buffers.push(byteNumbers.buffer);
    }

    return new Blob(buffers, { type: contentType });
  }

  toggleAddBudget(): void {
    this.showAddSingleProcedure = false;
    this.showAddBudget = !this.showAddBudget;
  }

  toggleAddSingleProcedure(): void {
    this.showAddBudget = false;
    this.showAddSingleProcedure = !this.showAddSingleProcedure;
  }
}
